import {
  ProposalFragment,
  ProposalStates,
  useGetPosProposalsLastLazyQuery,
} from "../../gql/hooks";
import { useLocalize } from "@france/superelements/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import useProposal from "../../redux/slices/proposal/hooks/useProposal";
import useOldProposal from "../../redux/slices/proposal/hooks/useOldProposal";
import useClearProposal from "../../redux/slices/proposal/hooks/useClearProposal";
import useProposalInformation from "../../redux/slices/proposal/hooks/useProposalInformation";

import { IonModal, useIonRouter } from "@ionic/react";
import { ButtonHighlight, Icon } from "@france/superelements/lib";
import SimpleMahouLoader from "../SimpleMahouLoader/SimpleMahouLoader";
import WarningModal from "../WarningModal/WarningModal";

import "./ProposalDraftModal.scss";
import ROUTES from "../../router/constants/routes";
import { formatDateFourDigits } from "../../utils/dateTime";

const ProposalDraftModal: React.FC<{
  posId: number | undefined;
  openStatus: boolean;
  onClose: () => void;
  redirect?: boolean;
  onNew?: () => void;
  onOld?: (id?: number) => void;
}> = ({ posId, openStatus, redirect, onClose, onNew, onOld }) => {
  // Hooks
  const t = useLocalize();
  const { push } = useIonRouter();
  const clearProposalState = useClearProposal();
  const { createProposal } = useProposal();
  const { onOldProposalUse, isLoading } = useOldProposal();
  const [loadLastProposal] = useGetPosProposalsLastLazyQuery();
  const { onPointOfSaleId, onCurrentStepUpdate } = useProposalInformation();

  // States
  const [draftProposal, setDraftProposal] = useState<ProposalFragment | null>(
    null,
  );
  const [isCreationError, setCreationError] = useState(false);

  // Memos
  const recapStep = useMemo(() => {
    let step = 1;
    if (!!draftProposal?.expireAt) {
      ++step;
      if (!!draftProposal?.brandId) {
        ++step;
      }
    }
    return step;
  }, [draftProposal?.brandId, draftProposal?.expireAt]);

  // Handlers
  const handleClose = useCallback(() => {
    onClose();
    setDraftProposal(null);
  }, [onClose]);

  const handleNew = useCallback(async () => {
    if (redirect && posId) {
      clearProposalState();
      await onPointOfSaleId(posId);
      const id = await createProposal();
      if (!id) {
        setCreationError(true);
        handleClose();
        return;
      }
      await onCurrentStepUpdate(1);
      push(`${ROUTES.RECOMMENDED_ASSORTMENT}`);
    } else {
      onNew?.();
    }
    handleClose();
  }, [
    posId,
    redirect,
    push,
    onNew,
    handleClose,
    createProposal,
    onPointOfSaleId,
    clearProposalState,
    onCurrentStepUpdate,
  ]);

  const handleOld = useCallback(async () => {
    if (redirect) {
      await onOldProposalUse(draftProposal?.id || 0);
    } else {
      onOld?.(draftProposal?.id);
    }
    handleClose();
  }, [draftProposal?.id, onOldProposalUse, handleClose, onOld, redirect]);

  const handleCloseError = useCallback(() => setCreationError(false), []);

  const handleRetry = useCallback(() => {
    setCreationError(false);
    handleNew();
  }, [handleNew]);

  const handleJudge = useCallback(async () => {
    if (!posId) return;

    const { data } = await loadLastProposal({
      variables: { posIds: [posId], take: 1, skip: 0 },
      fetchPolicy: "network-only",
    });

    if (data?.proposals?.[0]?.state === ProposalStates.Draft) {
      setDraftProposal(data?.proposals?.[0]);
    } else {
      handleNew();
    }
  }, [handleNew, loadLastProposal, posId]);

  // Effect
  useEffect(() => {
    if (openStatus && posId) {
      handleJudge();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openStatus]);

  return (
    <>
      <IonModal
        id="ProposalDraftModal"
        isOpen={openStatus}
        onWillDismiss={handleClose}
      >
        {openStatus && (!draftProposal || isLoading) && (
          <div className="loader">
            <SimpleMahouLoader />
          </div>
        )}
        {!!draftProposal && (
          <div className="container">
            <div className="head">{t("draft_exists")}</div>
            <div className="content">
              <span>{t("draft_exists_desc")}</span>
              <div className="box">
                <div className="title">
                  <Icon icon="document" />
                  <span>{draftProposal?.title}</span>
                </div>
                <div className="desc">
                  <div className="recapStep">{recapStep}</div>
                  <div className="text">{t("proposal_recap")}</div>
                  <div className="date">
                    {draftProposal.createdAt &&
                      formatDateFourDigits(draftProposal.createdAt)}
                  </div>
                </div>
              </div>
              <div className="warn">
                <Icon icon="info" />
                <span>{t("draft_override")}</span>
              </div>
            </div>
            <div className="footer">
              <ButtonHighlight name={t("create_new")} onClick={handleNew} />
              <ButtonHighlight name={t("take_over")} onClick={handleOld} />
            </div>
          </div>
        )}
      </IonModal>
      <WarningModal
        isOpen={isCreationError}
        onClose={handleCloseError}
        title={t("proposal_creation_error_title")}
        description={t("proposal_creation_error_desc")}
        onFirstAction={handleCloseError}
        firstActionWording={t("cancel")}
        onSecondAction={handleRetry}
        secondActionWording={t("try_again")}
      />
    </>
  );
};

export default ProposalDraftModal;
